import { type BoneDescriptor } from './SkeletonDescriptor'

/**
 * Pure validators for a custom skeleton's bone array. Run before saving a
 * skeleton or accepting one from a project archive.
 */

export const MAX_BONE_NAME_LENGTH = 64
export const MAX_BONES_PER_SKELETON = 256
export const BONE_NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/

export type ValidationResult =
  | { ok: true }
  | { ok: false, errors: string[] }

export function validate_bone_name (name: string): string | null {
  if (name.length === 0) return 'Bone name cannot be empty.'
  if (name.length > MAX_BONE_NAME_LENGTH) return `Bone name cannot exceed ${MAX_BONE_NAME_LENGTH} characters.`
  if (!BONE_NAME_REGEX.test(name)) return 'Bone name must start with a letter and contain only letters, digits, and underscores.'
  return null
}

export function validate_skeleton_name (name: string): string | null {
  if (name.trim().length === 0) return 'Skeleton name cannot be empty.'
  if (name.length > MAX_BONE_NAME_LENGTH) return `Skeleton name cannot exceed ${MAX_BONE_NAME_LENGTH} characters.`
  return null
}

export function validate_bones (bones: BoneDescriptor[]): ValidationResult {
  const errors: string[] = []

  if (bones.length === 0) {
    errors.push('Skeleton must contain at least one bone.')
  }
  if (bones.length > MAX_BONES_PER_SKELETON) {
    errors.push(`Skeleton cannot contain more than ${MAX_BONES_PER_SKELETON} bones.`)
  }

  const seen_names = new Set<string>()
  let root_count = 0

  for (let i = 0; i < bones.length; i++) {
    const bone = bones[i]
    const name_error = validate_bone_name(bone.name)
    if (name_error !== null) {
      errors.push(`Bone ${i}: ${name_error}`)
    }
    if (seen_names.has(bone.name)) {
      errors.push(`Duplicate bone name: "${bone.name}".`)
    } else {
      seen_names.add(bone.name)
    }

    if (bone.parent_index === null) {
      root_count++
    } else if (!Number.isInteger(bone.parent_index) || bone.parent_index < 0 || bone.parent_index >= bones.length) {
      errors.push(`Bone "${bone.name}" has out-of-range parent_index ${String(bone.parent_index)}.`)
    } else if (bone.parent_index === i) {
      errors.push(`Bone "${bone.name}" cannot be its own parent.`)
    }
  }

  if (root_count === 0 && bones.length > 0) {
    errors.push('Skeleton must contain at least one root bone (parent_index === null).')
  }

  if (has_cycle(bones)) {
    errors.push('Skeleton contains a parent cycle.')
  }

  if (errors.length > 0) return { ok: false, errors }
  return { ok: true }
}

function has_cycle (bones: BoneDescriptor[]): boolean {
  for (let start = 0; start < bones.length; start++) {
    const visited = new Set<number>()
    let cursor: number | null = start
    while (cursor !== null) {
      if (visited.has(cursor)) return true
      visited.add(cursor)
      const parent: number | null = bones[cursor].parent_index
      if (parent === null) break
      if (parent < 0 || parent >= bones.length) break
      cursor = parent
    }
  }
  return false
}

/**
 * Check whether `child_index` becoming a child of `proposed_parent_index` would
 * introduce a cycle. Used by reparent operations before they commit.
 */
export function would_create_cycle (
  bones: BoneDescriptor[],
  child_index: number,
  proposed_parent_index: number | null
): boolean {
  if (proposed_parent_index === null) return false
  if (proposed_parent_index === child_index) return true

  let cursor: number | null = proposed_parent_index
  const seen = new Set<number>()
  while (cursor !== null) {
    if (seen.has(cursor)) return true
    seen.add(cursor)
    if (cursor === child_index) return true
    if (cursor < 0 || cursor >= bones.length) return false
    cursor = bones[cursor].parent_index
  }
  return false
}
